import ReportChartPage, {
  ReportChartConfig,
} from '@app/components/reports/components/report-chart-page';
import {
  emissionsChartConfig,
  spendAxisProps,
  spendChartConfig,
  totalEmissionsAxisProps,
  totalWeightKgAxisProps,
  totalWeightKgChartConfig,
} from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import useAuth from '@app/auth/use-auth';
import { useAccount } from '@shared/api';
import DualAxisBarChart from '@shared/components/chart/dual-axis-bar-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import {
  getXAxisInterval,
  truncateLabel,
} from '@shared/components/chart/utils';
import Text from '@shared/components/content/text';
import { getCurrencySymbol } from '@shared/lib/format';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useState } from 'react';

interface EmissionsByCategoryProps extends ReportSectionComponent {}

export default function EmissionsByCategory({
  reportType,
}: EmissionsByCategoryProps) {
  const {
    result: { foodAndDrink },
    downloadMode,
  } = useGetReportSectionData(reportType);

  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;

  const { data: account } = useAccount(true);
  const currencySymbol = getCurrencySymbol(account?.defaultCurrency);

  const variants = {
    emissions: 'Emissions per category',
    weight: `Emissions vs ${reportTypeCopy.past} weight`,
    spend: 'Emissions vs spend',
  };

  const availableVariants = Object.values(variants).filter(
    (v) => v !== variants.spend || hasCostFeature
  );

  const [variant, setVariant] = useState<string>(variants.emissions);

  const emissionsByCategory = foodAndDrink?.emissionsByCategory.map((item) => ({
    ...item,
    category: capitalizeFirstLetter(item.category),
    totalWeightKg: item.totalWeightKg / 1000,
    totalSpend: item.totalSpend != null ? Number(item.totalSpend) : 0,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...(emissionsByCategory?.map((d) => d.totalEmissionsTonnes) ?? [])
  );
  const weightDataMax = Math.max(
    0,
    ...(emissionsByCategory?.map((d) => d.totalWeightKg) ?? [])
  );
  const spendDataMax = Math.max(
    0,
    ...(emissionsByCategory?.map((d) => d.totalSpend) ?? [])
  );

  const totalEmissionsChartConfig = {
    totalEmissionsTonnes: emissionsChartConfig.emissionsTonnes,
  };

  const defaultChartProps = {
    nameKey: 'category' as const,
    dataKey: 'category' as const,
    margin: { top: 40, right: 10, left: 0, bottom: 40 },
    className: 'h-max max-h-[calc(var(--content-title-height)-20rem)] w-full',
    XAxisProps: {
      interval: 0,
      angle: -30,
      textAnchor: 'end',
      tickMargin: 2,
      dx: 10,
      tickFormatter: (value: string) => truncateLabel(value, 15),
    },
  };

  let config: ReportChartConfig = totalEmissionsChartConfig;
  if (variant === variants.weight) {
    config = {
      ...totalEmissionsChartConfig,
      ...totalWeightKgChartConfig,
    };
  } else if (variant === variants.spend) {
    config = {
      ...totalEmissionsChartConfig,
      ...spendChartConfig(currencySymbol),
    };
  }

  return (
    <ReportChartPage
      title="Highest emission food categories"
      description={
        <Text variant="reportBody">
          This chart shows which food categories are contributing the most to
          your total emissions. The highest emissions categories are shown, with
          the remaining grouped into the <em>Other</em> category.
        </Text>
      }
      variantOptions={availableVariants}
      variant={variant}
      setVariant={setVariant}
      variantsEnabled={!downloadMode}
      chartConfig={config}
    >
      {variant === variants.emissions && (
        <SimpleBarChart
          {...defaultChartProps}
          chartConfig={totalEmissionsChartConfig}
          chartData={emissionsByCategory}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsByCategory?.length ?? 0),
          }}
        />
      )}
      {variant === variants.weight && (
        <DualAxisBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsByCategory}
          leftAxisConfig={totalEmissionsChartConfig}
          rightAxisConfig={totalWeightKgChartConfig(reportType)}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={totalWeightKgAxisProps(weightDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsByCategory?.length ?? 0),
          }}
        />
      )}
      {variant === variants.spend && (
        <DualAxisBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsByCategory}
          leftAxisConfig={totalEmissionsChartConfig}
          rightAxisConfig={spendChartConfig(currencySymbol)}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={spendAxisProps(spendDataMax, currencySymbol)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsByCategory?.length ?? 0),
          }}
        />
      )}
    </ReportChartPage>
  );
}
